import { getUUID } from '@shared/utils/global-id';

import { isFreeRecipient } from './recipientIds';
import type { TemplateSubject } from './templateSubject';
import type { EmailFormRecipient } from './types';

// An attachment already uploaded: the composer holds the stored file, not the
// browser File, so it survives the hop to the dialog as plain data.
export type ComposerDraftFile = {
  id: string;
  name: string;
  url: string;
  size: number;
  mimetype: string;
};

// What survives a closed composer: enough to rebuild the form, nothing that
// can be stale (the quote and threading come back from the message itself).
export type ComposerDraft = {
  subject: string;
  bodyHtml: string;
  to: EmailFormRecipient[];
  cc: EmailFormRecipient[];
  bcc: EmailFormRecipient[];
  files: ComposerDraftFile[];
  // The "Attach to" choice: the activity parents picked, and the contacts the
  // agent has already answered for. Without the second list a restored "Do
  // not attach" reads as no decision and the picker silently re-attaches.
  attachParentIds: string[];
  filingDecided: string[];
  // The applied template's subject, so it keeps following the recipients in
  // the composer the draft reopens in.
  templateSubject: TemplateSubject | null;
};

const PREFIX = 'crm.emailDraft.';

// One draft per composer context, per tab: sessionStorage dies with the tab,
// which is the decided lifetime (it survives navigation inside the CRM, not
// closing the tab). The key carries the user id so two accounts in the same
// tab never see each other's text.
export const composerDraftKey = (userId: string, context: string): string =>
  `${PREFIX}${userId}.${context}`;

type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const storage = (): DraftStorage | null => {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    // Blocked storage (privacy mode): no drafts, the composer still works.
    return null;
  }
};

// A restored id reaches `getUUID` in render (the template suggestions), which
// throws on a shape it cannot decode, with no error boundary above the
// composer: a stored id is a bare address or a decodable one.
const isRecipientId = (id: string): boolean => {
  if (isFreeRecipient({ id })) {
    return true;
  }
  try {
    getUUID(id);
    return true;
  } catch {
    return false;
  }
};

const isRecipient = (value: unknown): value is EmailFormRecipient =>
  typeof value === 'object' &&
  value != null &&
  typeof (value as { id?: unknown }).id === 'string' &&
  isRecipientId((value as { id: string }).id) &&
  typeof (value as { email?: unknown }).email === 'string';

const asRecipients = (value: unknown): EmailFormRecipient[] =>
  Array.isArray(value) ? value.filter(isRecipient) : [];

const isDraftFile = (value: unknown): value is ComposerDraftFile => {
  if (typeof value !== 'object' || value == null) {
    return false;
  }
  const file = value as Record<string, unknown>;
  return (
    typeof file.id === 'string' &&
    typeof file.name === 'string' &&
    typeof file.url === 'string' &&
    typeof file.size === 'number' &&
    typeof file.mimetype === 'string'
  );
};

const asDraftFiles = (value: unknown): ComposerDraftFile[] =>
  Array.isArray(value) ? value.filter(isDraftFile) : [];

const asTemplateSubject = (value: unknown): TemplateSubject | null => {
  if (typeof value !== 'object' || value == null) {
    return null;
  }
  const { raw, applied } = value as Record<string, unknown>;
  return typeof raw === 'string' && typeof applied === 'string'
    ? { raw, applied }
    : null;
};

const asIds = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((id): id is string => typeof id === 'string')
    : [];

// Parses defensively: the stored shape is ours, but a browser extension or
// an older build could have written anything under the key.
export const parseComposerDraft = (
  raw: string | null,
): ComposerDraft | null => {
  if (raw == null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed == null) {
      return null;
    }
    const draft = parsed as Record<string, unknown>;
    const subject = typeof draft.subject === 'string' ? draft.subject : '';
    const bodyHtml = typeof draft.bodyHtml === 'string' ? draft.bodyHtml : '';
    const to = asRecipients(draft.to);
    const cc = asRecipients(draft.cc);
    const bcc = asRecipients(draft.bcc);
    const files = asDraftFiles(draft.files);
    if (
      subject === '' &&
      bodyHtml === '' &&
      to.length + cc.length + bcc.length + files.length === 0
    ) {
      return null;
    }
    return {
      subject,
      bodyHtml,
      to,
      cc,
      bcc,
      files,
      attachParentIds: asIds(draft.attachParentIds),
      filingDecided: asIds(draft.filingDecided),
      templateSubject: asTemplateSubject(draft.templateSubject),
    };
  } catch {
    return null;
  }
};

export const isEmptyDraft = (
  draft: ComposerDraft,
  isBodyEmpty: boolean,
): boolean =>
  draft.subject.trim() === '' &&
  isBodyEmpty &&
  draft.to.length + draft.cc.length + draft.bcc.length + draft.files.length ===
    0;

export const readComposerDraft = (
  key: string,
  store: DraftStorage | null = storage(),
): ComposerDraft | null => {
  try {
    return parseComposerDraft(store?.getItem(key) ?? null);
  } catch {
    return null;
  }
};

export const writeComposerDraft = (
  key: string,
  draft: ComposerDraft,
  store: DraftStorage | null = storage(),
): void => {
  try {
    store?.setItem(key, JSON.stringify(draft));
  } catch {
    // Quota or blocked storage: the text stays in the open composer only.
  }
};

export const clearComposerDraft = (
  key: string,
  store: DraftStorage | null = storage(),
): void => {
  try {
    store?.removeItem(key);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
};
